import { useSleepSummary2 } from "@/components/worksheet/apis";
import { SleepSummary2 } from "@/components/worksheet/commons/sleep_summaries_2";
import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DiseaseRiskJudgementSection } from "./sleep_summaries_2/DiseaseRiskJudgementSection";
import JudgementSection, {
  SleepJudgementComparison,
} from "./sleep_summaries_2/JudgementSection";
import MeasurementDataAverageSection from "./sleep_summaries_2/MeasurementDataAverageSection";
import SleepConditionSection from "./sleep_summaries_2/SleepConditionSection";

/**
 * 睡眠評価ページ (レポートバージョン v4 以降用)
 * @returns 睡眠評価ページ
 */
export default function SleepSummaries2() {
  const { uuid } = useParams<{ uuid: string }>();
  const sleepSummary2Api = useSleepSummary2();
  const [sleepSummary2, setSleepSummary2] = useState<SleepSummary2 | null>(
    null
  );

  useEffect(() => {
    if (uuid == null) return;
    (async () => {
      const res = await sleepSummary2Api.get(uuid);
      setSleepSummary2(res.data);
    })();
  }, [uuid, sleepSummary2Api]);

  if (!sleepSummary2 || !uuid) {
    return <></>;
  }

  const sleepQuantityForJudgement: SleepJudgementComparison = {
    result: sleepSummary2.sleep_condition.sleep_quantity.result,
    last_time_result:
      sleepSummary2.sleep_condition.sleep_quantity.last_time_result,
    improvement: sleepSummary2.sleep_condition.sleep_quantity.improvement,
  };
  const sleepQualityForJudgement: SleepJudgementComparison = {
    result: sleepSummary2.sleep_condition.sleep_quality.result,
    last_time_result:
      sleepSummary2.sleep_condition.sleep_quality.last_time_result,
    improvement: sleepSummary2.sleep_condition.sleep_quality.improvement,
  };
  const sleepRhythmForJudgement: SleepJudgementComparison = {
    result: sleepSummary2.sleep_condition.sleep_rhythm.result,
    last_time_result:
      sleepSummary2.sleep_condition.sleep_rhythm.last_time_result,
    improvement: sleepSummary2.sleep_condition.sleep_rhythm.improvement,
  };
  // 受診勧奨オプションが ON かつ睡眠時無呼吸症候群及び不眠症のチェックが両方 ON の場合のみ
  // 疾患関連の表示を行う
  const isRecommendationExists: boolean = (
    sleepSummary2.judgement.comprehensive_evaluation.recommendation_existence
    && sleepSummary2.sleep_condition.sas !== null
    && sleepSummary2.sleep_condition.insomnia !== null
  );

  const SASForJudgement: SleepJudgementComparison | undefined = isRecommendationExists ? {
        result: sleepSummary2.sleep_condition.sas!.result,
        last_time_result: sleepSummary2.sleep_condition.sas!.last_time_result,
        improvement: sleepSummary2.sleep_condition.sas!.improvement,
      }
    : undefined;
  const insomniaForJudgement: SleepJudgementComparison | undefined = isRecommendationExists ? {
        result: sleepSummary2.sleep_condition.insomnia!.result,
        last_time_result:
          sleepSummary2.sleep_condition.insomnia!.last_time_result,
        improvement: sleepSummary2.sleep_condition.insomnia!.improvement,
      }
    : undefined;

  return (
    <Stack sx={{ gap: 20 }}>
      {isRecommendationExists ? (
        <JudgementSection
          uuid={uuid}
          judgement={sleepSummary2.judgement}
          quantityJudgement={sleepQuantityForJudgement}
          qualityJudgement={sleepQualityForJudgement}
          rhythmJudgement={sleepRhythmForJudgement}
          SASJudgement={SASForJudgement}
          insomniaJudgement={insomniaForJudgement}
          impactScores={sleepSummary2.impact_scores_with_sleep_quality}
        />
      ) : (
        <JudgementSection
          uuid={uuid}
          judgement={sleepSummary2.judgement}
          quantityJudgement={sleepQuantityForJudgement}
          qualityJudgement={sleepQualityForJudgement}
          rhythmJudgement={sleepRhythmForJudgement}
          impactScores={sleepSummary2.impact_scores_with_sleep_quality}
        />
      )
      }
      {isRecommendationExists && (
        <DiseaseRiskJudgementSection
          uuid={uuid}
          sas={sleepSummary2.sleep_condition.sas!}
          insomnia={sleepSummary2.sleep_condition.insomnia!}
          quantityResult={sleepSummary2.sleep_condition.sleep_quantity.result}
          qualityResult={sleepSummary2.sleep_condition.sleep_quality.result}
        />
      )}
      <SleepConditionSection
        uuid={uuid}
        sleep_quantity={sleepSummary2.sleep_condition.sleep_quantity}
        sleep_quality={sleepSummary2.sleep_condition.sleep_quality}
        sleep_rhythm={sleepSummary2.sleep_condition.sleep_rhythm}
      />
      <MeasurementDataAverageSection
        measurement_data={sleepSummary2.sleep_condition.measurement_data}
      />
    </Stack>
  );
}
